import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { saveRecipe, unsaveRecipe, getSavedRecipeIds } from '../utils/recipeService';
import { track } from '../utils/analytics';

// One set of saved recipe ids for the whole session.
//
// The alternative was an isSaved() read per bookmark button, which on a
// twenty-card browse grid is twenty document reads just to decide which icon
// to draw. This is one query at sign-in instead, and every card reads from it.
//
// Toggling is optimistic: the icon flips immediately and rolls back if the
// write fails. A bookmark is low-stakes enough that waiting on a round trip
// costs more than the rare rollback does.

const EMPTY = new Set();

const SavedRecipesContext = createContext(null);

export function useSavedRecipes() {
  const ctx = useContext(SavedRecipesContext);
  // Deliberately not throwing: RecipeCard renders on public pages that
  // anonymous visitors reach, and a missing provider there should degrade to
  // "no bookmarks" rather than blanking the page.
  return ctx || { savedIds: EMPTY, isSaved: () => false, toggleSave: async () => {}, ready: false };
}

export function SavedRecipesProvider({ children }) {
  const { currentUser } = useAuth();
  const [savedIds, setSavedIds] = useState(EMPTY);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    if (!currentUser) {
      setSavedIds(EMPTY);
      setReady(true);
      return undefined;
    }
    setReady(false);
    getSavedRecipeIds(currentUser.uid)
      .then(ids => { if (active) setSavedIds(new Set(ids)); })
      .catch(err => {
        console.error('Error loading saved recipes:', err);
        if (active) setSavedIds(EMPTY);
      })
      .finally(() => { if (active) setReady(true); });
    return () => { active = false; };
  }, [currentUser]);

  const isSaved = useCallback((recipeId) => savedIds.has(recipeId), [savedIds]);

  const toggleSave = useCallback(async (recipeId) => {
    if (!currentUser || !recipeId) return false;
    const next = !savedIds.has(recipeId);

    setSavedIds(prev => {
      const copy = new Set(prev);
      if (next) copy.add(recipeId); else copy.delete(recipeId);
      return copy;
    });

    try {
      if (next) await saveRecipe(recipeId, currentUser.uid);
      else await unsaveRecipe(recipeId, currentUser.uid);
      // No recipe id, title or ingredient ever goes to analytics -- the
      // direction of the toggle is the whole event.
      track(next ? 'recipe_save' : 'recipe_unsave');
    } catch (err) {
      console.error('Error toggling save:', err);
      setSavedIds(prev => {
        const copy = new Set(prev);
        if (next) copy.delete(recipeId); else copy.add(recipeId);
        return copy;
      });
      throw err;
    }
    return next;
  }, [currentUser, savedIds]);

  return (
    <SavedRecipesContext.Provider value={{ savedIds, isSaved, toggleSave, ready }}>
      {children}
    </SavedRecipesContext.Provider>
  );
}
